import { useEffect, useState } from 'react';
import type {
  PartnerCommissionModel,
  PartnerDetail,
  PartnerListItem,
  PartnerStatus,
  PartnerType,
  PartnerUserListItem,
  PartnerUserRole,
} from '@accestra/types';
import {
  Button,
  C,
  Card,
  Field,
  Pill,
  TableWrap,
  asNullableText,
  friendlyError,
  formatMaybeDateTime,
  formatMoney,
  inputStyle,
  paFetch,
  statusColor,
  tdS,
  textareaStyle,
  thS,
  titleCase,
  toNullableNumber,
} from './shared';

const PARTNER_TYPE_OPTIONS: PartnerType[] = [
  'agency',
  'consultant',
  'accessibility_specialist',
  'reseller',
  'other',
];

const COMMISSION_MODEL_OPTIONS: PartnerCommissionModel[] = [
  'recurring_percent',
  'one_time_percent',
  'one_time_fixed',
];

const PARTNER_STATUS_FILTERS: Array<PartnerStatus | 'all'> = [
  'all',
  'active',
  'paused',
  'applicant',
  'rejected',
  'terminated',
];

const PARTNER_USER_ROLE_OPTIONS: PartnerUserRole[] = ['owner', 'member'];

interface PartnerListResponse {
  partners: PartnerListItem[];
  total: number;
}

interface PartnerUsersResponse {
  users: PartnerUserListItem[];
}

interface PartnerDemoWorkspaceResponse {
  demoWorkspace: NonNullable<PartnerDetail['demoWorkspace']>;
}

function emptyPartnerForm() {
  return {
    name: '',
    legalName: '',
    contactName: '',
    email: '',
    phone: '',
    websiteUrl: '',
    linkedinUrl: '',
    country: '',
    partnerType: 'agency' as PartnerType,
    commissionModel: 'recurring_percent' as PartnerCommissionModel,
    commissionPercent: '',
    commissionDurationMonths: '',
    notes: '',
  };
}

function emptyPartnerUserForm() {
  return {
    email: '',
    role: 'member' as PartnerUserRole,
  };
}

export default function PartnersPanel() {
  const [statusFilter, setStatusFilter] = useState<PartnerStatus | 'all'>('all');
  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDetail, setSelectedDetail] = useState<PartnerDetail | null>(null);
  const [partnerUsers, setPartnerUsers] = useState<PartnerUserListItem[]>([]);
  const [partnerForm, setPartnerForm] = useState(emptyPartnerForm);
  const [partnerUserForm, setPartnerUserForm] = useState(emptyPartnerUserForm);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  async function loadPartners() {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        page: '1',
        pageSize: '100',
      });
      if (statusFilter !== 'all') {
        query.set('status', statusFilter);
      }

      const result = await paFetch<PartnerListResponse>('GET', `/platform-admin/partners?${query.toString()}`);
      setPartners(result.partners);
      if (!result.partners.find((partner) => partner.id === selectedId)) {
        setSelectedId(result.partners[0]?.id ?? null);
      }
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(partnerId: string) {
    try {
      const detail = await paFetch<PartnerDetail>('GET', `/platform-admin/partners/${partnerId}`);
      setSelectedDetail(detail);
    } catch (nextError) {
      setError(friendlyError(nextError));
    }
  }

  async function loadPartnerUsers(partnerId: string) {
    try {
      const result = await paFetch<PartnerUsersResponse>('GET', `/platform-admin/partners/${partnerId}/users`);
      setPartnerUsers(result.users);
    } catch (nextError) {
      setError(friendlyError(nextError));
    }
  }

  useEffect(() => {
    void loadPartners();
  }, [statusFilter]);

  useEffect(() => {
    if (selectedId) {
      void loadDetail(selectedId);
      void loadPartnerUsers(selectedId);
    } else {
      setSelectedDetail(null);
      setPartnerUsers([]);
    }
  }, [selectedId]);

  useEffect(() => {
    if (!selectedDetail) {
      return;
    }

    const partner = selectedDetail.partner;
    setPartnerForm({
      name: partner.name,
      legalName: partner.legalName ?? '',
      contactName: partner.contactName ?? '',
      email: partner.email,
      phone: partner.phone ?? '',
      websiteUrl: partner.websiteUrl ?? '',
      linkedinUrl: partner.linkedinUrl ?? '',
      country: partner.country ?? '',
      partnerType: partner.partnerType,
      commissionModel: partner.commissionModel,
      commissionPercent: partner.commissionPercent?.toString() ?? '',
      commissionDurationMonths: partner.commissionDurationMonths?.toString() ?? '',
      notes: partner.notes ?? '',
    });
  }, [selectedDetail]);

  async function savePartner() {
    if (!selectedDetail) {
      return;
    }

    setBusyAction('save');
    setFeedback(null);
    setError(null);
    try {
      await paFetch(
        'PATCH',
        `/platform-admin/partners/${selectedDetail.partner.id}`,
        {
          name: partnerForm.name.trim(),
          legalName: asNullableText(partnerForm.legalName),
          contactName: asNullableText(partnerForm.contactName),
          email: partnerForm.email.trim(),
          phone: asNullableText(partnerForm.phone),
          websiteUrl: asNullableText(partnerForm.websiteUrl),
          linkedinUrl: asNullableText(partnerForm.linkedinUrl),
          country: asNullableText(partnerForm.country),
          partnerType: partnerForm.partnerType,
          commissionModel: partnerForm.commissionModel,
          commissionPercent: toNullableNumber(partnerForm.commissionPercent),
          commissionDurationMonths: toNullableNumber(partnerForm.commissionDurationMonths),
          notes: asNullableText(partnerForm.notes),
        },
      );
      await loadPartners();
      await loadDetail(selectedDetail.partner.id);
      setFeedback('Partner updated.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  async function setPartnerStatus(nextStatus: 'active' | 'paused') {
    if (!selectedDetail) {
      return;
    }

    setBusyAction(nextStatus);
    setFeedback(null);
    setError(null);
    try {
      await paFetch('POST', `/platform-admin/partners/${selectedDetail.partner.id}/${nextStatus === 'active' ? 'activate' : 'pause'}`);
      await loadPartners();
      await loadDetail(selectedDetail.partner.id);
      setFeedback(`Partner ${nextStatus === 'active' ? 'activated' : 'paused'}.`);
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  async function copyReferralCode(referralCode: string) {
    try {
      await navigator.clipboard.writeText(referralCode);
      setFeedback(`Copied referral code ${referralCode}.`);
      setError(null);
    } catch {
      setError('Could not copy the referral code.');
    }
  }

  async function linkUserToPartner() {
    if (!selectedDetail) {
      return;
    }

    setBusyAction('link-user');
    setFeedback(null);
    setError(null);
    try {
      const result = await paFetch<PartnerUsersResponse>(
        'POST',
        `/platform-admin/partners/${selectedDetail.partner.id}/users`,
        {
          email: partnerUserForm.email.trim(),
          role: partnerUserForm.role,
        },
      );
      setPartnerUsers(result.users);
      setPartnerUserForm(emptyPartnerUserForm());
      await loadDetail(selectedDetail.partner.id);
      setFeedback('User linked to partner.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  async function createDemoWorkspace() {
    if (!selectedDetail) {
      return;
    }

    setBusyAction('create-demo-workspace');
    setFeedback(null);
    setError(null);
    try {
      await paFetch<PartnerDemoWorkspaceResponse>(
        'POST',
        `/platform-admin/partners/${selectedDetail.partner.id}/demo-workspace`,
      );
      await loadDetail(selectedDetail.partner.id);
      await loadPartnerUsers(selectedDetail.partner.id);
      setFeedback('Demo workspace created and seeded.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(420px, 1fr)', gap: 18 }}>
      <Card
        title="Partners"
        subtitle="Open partner records, review commercial posture, and track top-line referral and commission summaries."
        action={(
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as PartnerStatus | 'all')}
              style={inputStyle}
            >
              {PARTNER_STATUS_FILTERS.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All statuses' : titleCase(status)}
                </option>
              ))}
            </select>
            <Button onClick={() => void loadPartners()}>Refresh</Button>
          </div>
        )}
      >
        {loading ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Loading partners…</div>
        ) : (
          <TableWrap>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Partner</th>
                  <th style={thS}>Status</th>
                  <th style={thS}>Referrals</th>
                  <th style={thS}>Pending</th>
                </tr>
              </thead>
              <tbody>
                {partners.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...tdS, textAlign: 'center', color: C.muted }}>No partners found.</td>
                  </tr>
                ) : partners.map((partner) => (
                  <tr
                    key={partner.id}
                    onClick={() => setSelectedId(partner.id)}
                    style={{
                      cursor: 'pointer',
                      background: selectedId === partner.id ? 'rgba(14, 165, 164, 0.06)' : 'transparent',
                    }}
                  >
                    <td style={tdS}>
                      <div style={{ fontWeight: 760 }}>{partner.name}</div>
                      <div style={{ color: C.muted, fontSize: 12 }}>{partner.email}</div>
                    </td>
                    <td style={tdS}><Pill label={titleCase(partner.status)} tone={statusColor(partner.status)} /></td>
                    <td style={tdS}>{partner.referralCount}</td>
                    <td style={tdS}>{partner.pendingCommissionAmount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card
        title="Partner detail"
        subtitle="Edit the selected partner, copy the referral code, and review related referrals, commissions, and payouts."
      >
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', color: C.danger, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}
        {feedback && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 12px', color: C.success, fontSize: 13, marginBottom: 12 }}>
            {feedback}
          </div>
        )}

        {!selectedDetail ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Select a partner to review.</div>
        ) : (
          <div style={{ display: 'grid', gap: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 780, color: C.text }}>{selectedDetail.partner.name}</div>
                <div style={{ color: C.muted, fontSize: 13 }}>{selectedDetail.partner.email}</div>
              </div>
              <Pill label={titleCase(selectedDetail.partner.status)} tone={statusColor(selectedDetail.partner.status)} />
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <Field label="Referral code">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input style={{ ...inputStyle, fontWeight: 760 }} value={selectedDetail.partner.referralCode} readOnly />
                  <Button onClick={() => void copyReferralCode(selectedDetail.partner.referralCode)}>Copy</Button>
                </div>
              </Field>

              <div
                style={{
                  display: 'grid',
                  gap: 12,
                  padding: 14,
                  borderRadius: 16,
                  border: `1px solid ${C.border}`,
                  background: '#fafcfe',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 760, color: C.text }}>Partner access</div>
                  <div style={{ color: C.muted, fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
                    Link an existing user account to this partner so they can access the partner portal.
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 140px auto', gap: 12, alignItems: 'end' }}>
                  <Field label="User email">
                    <input
                      style={inputStyle}
                      value={partnerUserForm.email}
                      onChange={(event) => setPartnerUserForm((current) => ({ ...current, email: event.target.value }))}
                      placeholder="user@example.com"
                    />
                  </Field>
                  <Field label="Role">
                    <select
                      style={inputStyle}
                      value={partnerUserForm.role}
                      onChange={(event) => setPartnerUserForm((current) => ({ ...current, role: event.target.value as PartnerUserRole }))}
                    >
                      {PARTNER_USER_ROLE_OPTIONS.map((role) => (
                        <option key={role} value={role}>
                          {titleCase(role)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Button
                    variant="primary"
                    disabled={busyAction !== null || !partnerUserForm.email.trim()}
                    onClick={() => void linkUserToPartner()}
                  >
                    {busyAction === 'link-user' ? 'Linking…' : 'Link user to partner'}
                  </Button>
                </div>

                <div style={{ display: 'grid', gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 760, color: C.text }}>
                    Linked users ({partnerUsers.length})
                  </div>
                  {partnerUsers.length === 0 ? (
                    <div style={{ color: C.muted, fontSize: 12.5 }}>No linked users yet.</div>
                  ) : partnerUsers.map((user) => (
                    <div
                      key={user.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        alignItems: 'center',
                        padding: '10px 12px',
                        borderRadius: 12,
                        border: `1px solid ${C.border}`,
                        background: '#fff',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 760, color: C.text }}>
                          {user.userFullName}
                        </div>
                        <div style={{ color: C.muted, fontSize: 12 }}>{user.userEmail}</div>
                      </div>
                      <Pill label={titleCase(user.role)} tone={statusColor(user.role)} />
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gap: 12,
                  padding: 14,
                  borderRadius: 16,
                  border: `1px solid ${C.border}`,
                  background: '#fcfcfd',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 760, color: C.text }}>Demo workspace</div>
                    <div style={{ color: C.muted, fontSize: 12.5, lineHeight: 1.6, marginTop: 4 }}>
                      Create one founder-seeded sample workspace for this partner so they can run live product demos without using a shared global account.
                    </div>
                  </div>
                  {selectedDetail.demoWorkspace && (
                    <Pill label="Created" tone="#15803d" />
                  )}
                </div>

                {selectedDetail.demoWorkspace ? (
                  <div
                    style={{
                      display: 'grid',
                      gap: 10,
                      padding: 12,
                      borderRadius: 14,
                      border: `1px solid ${C.border}`,
                      background: '#fff',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 760, color: C.text }}>
                        {selectedDetail.demoWorkspace.organisationName}
                      </div>
                      <div style={{ color: C.muted, fontSize: 12 }}>
                        {selectedDetail.demoWorkspace.organisationSlug}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Pill label={`${selectedDetail.demoWorkspace.siteCount} sites`} tone="#0f766e" />
                      <Pill label={`${selectedDetail.demoWorkspace.scanCount} scans`} tone="#0f766e" />
                      <Pill label={`${selectedDetail.demoWorkspace.memberCount} members`} tone="#0f766e" />
                      <Pill label={`Seed ${selectedDetail.demoWorkspace.seedVersion}`} tone="#64748b" />
                    </div>
                    <div style={{ color: C.muted, fontSize: 12.5 }}>
                      Seeded {formatMaybeDateTime(selectedDetail.demoWorkspace.seededAt)}. Linked partner users are provisioned into this workspace automatically.
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ color: C.muted, fontSize: 12.5 }}>
                      The workspace will be created on the existing organisation model, marked as a demo/sample workspace, and seeded with realistic sites, scans, issues, and HTML evidence.
                    </div>
                    {partnerUsers.length === 0 && (
                      <div style={{ color: C.warning, fontSize: 12.5 }}>
                        Link at least one partner user first so someone can access the demo workspace immediately after creation.
                      </div>
                    )}
                    <div>
                      <Button
                        variant="primary"
                        disabled={busyAction !== null || partnerUsers.length === 0}
                        onClick={() => void createDemoWorkspace()}
                      >
                        {busyAction === 'create-demo-workspace' ? 'Creating…' : 'Create demo workspace'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                <Field label="Name">
                  <input style={inputStyle} value={partnerForm.name} onChange={(event) => setPartnerForm((current) => ({ ...current, name: event.target.value }))} />
                </Field>
                <Field label="Contact name">
                  <input style={inputStyle} value={partnerForm.contactName} onChange={(event) => setPartnerForm((current) => ({ ...current, contactName: event.target.value }))} />
                </Field>
                <Field label="Email">
                  <input style={inputStyle} value={partnerForm.email} onChange={(event) => setPartnerForm((current) => ({ ...current, email: event.target.value }))} />
                </Field>
                <Field label="Phone">
                  <input style={inputStyle} value={partnerForm.phone} onChange={(event) => setPartnerForm((current) => ({ ...current, phone: event.target.value }))} />
                </Field>
                <Field label="Website">
                  <input style={inputStyle} value={partnerForm.websiteUrl} onChange={(event) => setPartnerForm((current) => ({ ...current, websiteUrl: event.target.value }))} />
                </Field>
                <Field label="LinkedIn">
                  <input style={inputStyle} value={partnerForm.linkedinUrl} onChange={(event) => setPartnerForm((current) => ({ ...current, linkedinUrl: event.target.value }))} />
                </Field>
                <Field label="Country">
                  <input style={inputStyle} value={partnerForm.country} onChange={(event) => setPartnerForm((current) => ({ ...current, country: event.target.value }))} />
                </Field>
                <Field label="Legal name">
                  <input style={inputStyle} value={partnerForm.legalName} onChange={(event) => setPartnerForm((current) => ({ ...current, legalName: event.target.value }))} />
                </Field>
                <Field label="Partner type">
                  <select style={inputStyle} value={partnerForm.partnerType} onChange={(event) => setPartnerForm((current) => ({ ...current, partnerType: event.target.value as PartnerType }))}>
                    {PARTNER_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
                  </select>
                </Field>
                <Field label="Commission model">
                  <select style={inputStyle} value={partnerForm.commissionModel} onChange={(event) => setPartnerForm((current) => ({ ...current, commissionModel: event.target.value as PartnerCommissionModel }))}>
                    {COMMISSION_MODEL_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
                  </select>
                </Field>
                <Field label="Commission percent">
                  <input style={inputStyle} value={partnerForm.commissionPercent} onChange={(event) => setPartnerForm((current) => ({ ...current, commissionPercent: event.target.value }))} />
                </Field>
                <Field label="Commission duration (months)">
                  <input style={inputStyle} value={partnerForm.commissionDurationMonths} onChange={(event) => setPartnerForm((current) => ({ ...current, commissionDurationMonths: event.target.value }))} />
                </Field>
              </div>

              <Field label="Notes">
                <textarea style={textareaStyle} value={partnerForm.notes} onChange={(event) => setPartnerForm((current) => ({ ...current, notes: event.target.value }))} />
              </Field>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="primary" disabled={busyAction !== null} onClick={() => void savePartner()}>
                {busyAction === 'save' ? 'Saving…' : 'Save changes'}
              </Button>
              {selectedDetail.partner.status !== 'active' && (
                <Button disabled={busyAction !== null} onClick={() => void setPartnerStatus('active')}>
                  {busyAction === 'active' ? 'Activating…' : 'Activate'}
                </Button>
              )}
              {selectedDetail.partner.status === 'active' && (
                <Button variant="ghost" disabled={busyAction !== null} onClick={() => void setPartnerStatus('paused')}>
                  {busyAction === 'paused' ? 'Pausing…' : 'Pause'}
                </Button>
              )}
            </div>

            <div style={{ display: 'grid', gap: 14, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 15, fontWeight: 760, color: C.text }}>Related activity</div>
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 760 }}>Referrals ({selectedDetail.referrals.length})</div>
                {selectedDetail.referrals.length === 0 ? (
                  <div style={{ color: C.muted, fontSize: 12 }}>No referrals yet.</div>
                ) : selectedDetail.referrals.slice(0, 5).map((referral) => (
                  <div key={referral.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5 }}>
                    <span>{referral.prospectCompany || referral.organisationName || 'Unnamed referral'}</span>
                    <Pill label={titleCase(referral.status)} tone={statusColor(referral.status)} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 760 }}>Commissions ({selectedDetail.commissions.length})</div>
                {selectedDetail.commissions.length === 0 ? (
                  <div style={{ color: C.muted, fontSize: 12 }}>No commissions yet.</div>
                ) : selectedDetail.commissions.slice(0, 5).map((commission) => (
                  <div key={commission.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5 }}>
                    <span>{formatMoney(commission.commissionAmount, commission.currency)} · {commission.organisationName || 'Manual attribution'}</span>
                    <Pill label={titleCase(commission.commissionStatus)} tone={statusColor(commission.commissionStatus)} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 760 }}>Payouts ({selectedDetail.payouts.length})</div>
                {selectedDetail.payouts.length === 0 ? (
                  <div style={{ color: C.muted, fontSize: 12 }}>No payouts yet.</div>
                ) : selectedDetail.payouts.slice(0, 5).map((payout) => (
                  <div key={payout.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5 }}>
                    <span>{formatMoney(payout.totalAmount, payout.currency)} · {formatMaybeDateTime(payout.createdAt)}</span>
                    <Pill label={titleCase(payout.status)} tone={statusColor(payout.status)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
